import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userAPI, petGuardianshipAPI } from '../../services/api';
import { showErrorToast, showSuccessToast } from '../../utils/notificationUtils';
import { getUserPlaceholderImage } from '../../utils/imageUtils';

const InviteGuardianModal = ({ isOpen, onClose, petId, currentGuardians = [], onInviteSent, primaryColor = '#8548A8' }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inviting, setInviting] = useState({});

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSearchResults([]);
        }
    }, [isOpen]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchQuery.length >= 3) {
                setLoading(true);
                try {
                    const response = await userAPI.searchUsers({ query: searchQuery });
                    // Handle both array and object response formats from searchUsers
                    const users = Array.isArray(response.data)
                        ? response.data
                        : (response.data?.data || []);

                    setSearchResults(users);
                } catch (error) {
                    console.error('Error searching users:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const handleInvite = async (user) => {
        // Handle both id and user_id fields (API inconsistency)
        const userId = user.id || user.user_id;

        setInviting(prev => ({ ...prev, [userId]: true }));
        try {
            // Send only user_id as the backend expects either email OR user_id
            const payload = { user_id: userId };
            console.log('Inviting guardian:', { petId, payload, user });

            await petGuardianshipAPI.inviteGuardian(petId, payload);
            showSuccessToast(t('petProfile.guardians.inviteModal.inviteSent'));
            if (onInviteSent) onInviteSent();
            // Don't close immediately so they can invite more guardians
        } catch (error) {
            console.error('Error inviting guardian:', error);
            console.error('Error response:', error.response?.data);

            // Extract validation errors if available
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.errors?.user_id?.[0] ||
                t('petProfile.guardians.inviteModal.errorSending');
            showErrorToast(errorMessage);
        } finally {
            const userId = user.id || user.user_id;
            setInviting(prev => ({ ...prev, [userId]: false }));
        }
    };

    const isAlreadyGuardian = (userId) => {
        return currentGuardians.some(g => {
            // Prioritize user_id if available (e.g. if g is a pivot/relation object)
            // Otherwise assume g is a User object and use g.id
            const guardianId = g.user_id || g.id;
            return String(guardianId) === String(userId);
        });
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }} onClick={onClose}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                        {t('petProfile.guardians.inviteModal.title')}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#6B7280'
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <input
                            type="text"
                            placeholder={t('petProfile.guardians.inviteModal.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid #E5E7EB',
                                fontSize: '15px'
                            }}
                            autoFocus
                        />
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                            {t('petProfile.guardians.inviteModal.searching')}
                        </div>
                    ) : searchResults.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {searchResults.map(user => {
                                const userId = user.id || user.user_id;
                                const isGuardian = isAlreadyGuardian(userId);
                                const displayName = user.name || user.full_name || user.display_name ||
                                    (user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.username);

                                return (
                                    <div key={user.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        background: '#F9FAFB',
                                        border: '1px solid #F3F4F6'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <img
                                                src={user.profile_image_url || getUserPlaceholderImage(user)}
                                                alt={displayName}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = getUserPlaceholderImage(user);
                                                }}
                                                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 500, color: '#111827' }}>
                                                    {displayName}
                                                </div>


                                            </div>
                                        </div>

                                        {isGuardian ? (
                                            <span style={{
                                                fontSize: '13px',
                                                color: '#10B981',
                                                background: '#D1FAE5',
                                                padding: '4px 8px',
                                                borderRadius: '4px'
                                            }}>
                                                {t('petProfile.guardians.inviteModal.alreadyGuardian')}
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => handleInvite(user)}
                                                disabled={inviting[user.id]}
                                                style={{
                                                    padding: '6px 12px',
                                                    background: inviting[user.id] ? '#9CA3AF' : primaryColor,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '13px',
                                                    fontWeight: 500,
                                                    cursor: inviting[user.id] ? 'default' : 'pointer'
                                                }}
                                            >
                                                {inviting[user.id] ? '...' : t('petProfile.guardians.inviteModal.sendInvite')}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : searchQuery.length >= 3 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                            {t('petProfile.guardians.inviteModal.noUsersFound')}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default InviteGuardianModal;
